import * as Sentry from '@sentry/react';
import type { Query, Mutation } from '@tanstack/react-query';
import {
  getAxiosStatus,
  isCancelledError,
  isSentryCaptured,
  isSentryInitialized,
  markSentryCaptured,
  normalizeError,
} from '@payler/sentry/merchant-cabinet';

function getKeyRoot(queryKey: unknown): string | undefined {
  if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') return queryKey[0];
  if (typeof queryKey === 'string') return queryKey;
  return undefined;
}

export function captureReactQueryError(
  error: unknown,
  ctx:
    | { kind: 'query'; query: Query<unknown, unknown, unknown, any> }
    | { kind: 'mutation'; mutation: Mutation<unknown, unknown, unknown, any> },
): void {
  if (!isSentryInitialized()) return;

  if (isSentryCaptured(error)) return;
  if (isCancelledError(error)) return;

  const status = getAxiosStatus(error);

  if (typeof status === 'number' && status < 500) {
    return;
  }

  try {
    Sentry.withScope((scope) => {
      scope.setLevel(typeof status === 'number' ? 'error' : 'error');
      scope.setTag('error.kind', 'react_query_error');
      scope.setTag('rq.kind', ctx.kind);
      if (typeof status === 'number') scope.setTag('http.status_code', String(status));

      if (ctx.kind === 'query') {
        const keyRoot = getKeyRoot(ctx.query.queryKey);
        if (keyRoot) scope.setTag('rq.query', keyRoot);
      }
      if (ctx.kind === 'mutation') {
        const keyRoot = getKeyRoot(ctx.mutation.options.mutationKey);
        if (keyRoot) scope.setTag('rq.mutation', keyRoot);
      }

      scope.setContext('rq', {
        kind: ctx.kind,
        // Only non-sensitive bits:
        queryKeyRoot: ctx.kind === 'query' ? getKeyRoot(ctx.query.queryKey) : undefined,
        mutationKeyRoot:
          ctx.kind === 'mutation' ? getKeyRoot(ctx.mutation.options.mutationKey) : undefined,
      });

      Sentry.captureException(normalizeError(error));
    });

    markSentryCaptured(error, 'react-query');
  } catch {
    // never break app flow
  }
}

